/**
 * VH-01 (Doom's R1 review, Exhibit 4 green box): the volatility harvester's own control, under the game rows — its own
 * on/off, clip and limits, separate from Penelope's. Small quotes on both sides joining the touch where they lock, held
 * through liquidity withdrawal, a filled side reposted only once the book's depth comes back (relay harvest.ts).
 *
 * PRACTICE ONLY: D-065 approves scoping; live testing needs its own authorization, so the relay parks a live harvester
 * unless PENELOPE_HARVESTER_LIVE_ALLOWED is set. Collapsed by default — it reads the relay only while it is open.
 */
import { useState } from 'react';
import { Waves } from 'lucide-react';
import { cn } from '@/lib/infra/utils';
import { penelopeEventTicker, type PenelopeConfig } from '@/hooks/usePenelope';
import { usePenelopeAccess } from '@/hooks/usePenelopeAccess';
import { PENELOPE_EVENT_PREFIXES, usePenelopeSides } from '@/hooks/usePenelopeSides';
import { penelopeStatusSentence } from '@/views/LiveGame/penelopeStatus';
import type { GameRow } from '@/views/ValueDashboard/types';

const OPEN_KEY = 'g1.harvesterControl.open';
const readOpen = (): boolean => {
  try { return localStorage.getItem(OPEN_KEY) === '1'; } catch { return false; }
};
const writeOpen = (open: boolean): void => {
  try { localStorage.setItem(OPEN_KEY, open ? '1' : '0'); } catch { /* per-viewer convenience only */ }
};

/** The relay's harvester start (config.ts HARVESTER_START), shown before a row exists. */
const START: Partial<PenelopeConfig> = { clipContracts: 2, maxNetContracts: 10, harvestRecoverDepth: 25_000 };
const FIELDS: Array<{ key: 'clipContracts' | 'maxNetContracts' | 'harvestRecoverDepth' | 'harvestRecoverPersistMs'; label: string; hint: string; step: number }> = [
  { key: 'clipContracts', label: 'clip', hint: 'Contracts per quote — small by design.', step: 1 },
  { key: 'maxNetContracts', label: 'max net', hint: 'How far long one team it may run while a pair is unmatched.', step: 1 },
  { key: 'harvestRecoverDepth', label: 'recover depth', hint: 'After a side fills it quotes again only once this many contracts rest on that book\'s top four bid levels (its own order not counted).', step: 1000 },
  { key: 'harvestRecoverPersistMs', label: 'recover hold', hint: 'That depth must stay back this long, in milliseconds, before it posts again — a brief flicker does not count (the relay\'s default when blank).', step: 500 },
];

export function HarvesterControl({ game }: { game: GameRow }) {
  const access = usePenelopeAccess();
  const event = penelopeEventTicker(game);
  const supported = event !== null && PENELOPE_EVENT_PREFIXES.some((p) => event.startsWith(p));
  const [open, setOpen] = useState(readOpen);
  const [draft, setDraft] = useState<Partial<PenelopeConfig>>(START);
  const sides = usePenelopeSides(game, access && supported && open);
  if (!access || !supported) return null;
  const toggle = () => { setOpen(!open); writeOpen(!open); };
  if (!open) {
    return (
      <button type="button" onClick={toggle} data-testid="harvester-open"
        className="border-border/60 text-muted-foreground hover:text-foreground mt-1 flex items-center gap-1 rounded border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide"
        title="The volatility harvester: small quotes on both sides, held through liquidity drops (practice only)">
        <Waves className="h-3 w-3" /> volatility harvester
      </button>
    );
  }
  const control = sides.control;
  const h = control.harvester;
  const row = h?.game ?? null;
  const cfg = (row?.config ?? draft) as Partial<PenelopeConfig>;
  const armed = row?.config.enabled === true;
  const live = row?.live?.baseline ?? null;
  const sentence = row
    ? penelopeStatusSentence({ armed, parked: row.parked, practice: row.config.mode !== 'live', teamA: game.awayCode, teamB: game.homeCode, live })
    : 'not started';
  const small = 'rounded border px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide disabled:opacity-50';
  return (
    <div className="border-border/60 mt-1 space-y-1.5 rounded border p-2 text-[11px]" data-testid="harvester-control">
      <div className="flex items-center gap-2">
        <Waves className="text-muted-foreground h-3.5 w-3.5" />
        <span className="font-semibold">Volatility harvester</span>
        <span className="rounded border border-sky-500 px-1.5 py-px font-mono text-[9px] font-bold uppercase text-sky-500">practice</span>
        <span className="flex-1" />
        <button type="button" onClick={toggle} className="text-muted-foreground hover:text-foreground text-[10px] uppercase">close</button>
      </div>
      <p className={cn(armed ? 'text-foreground' : 'text-muted-foreground')} data-testid="harvester-status">{sentence}</p>
      {live && (
        <p className="text-muted-foreground font-mono text-[10px]">
          held {live.inv.A.contracts} {game.awayCode} / {live.inv.B.contracts} {game.homeCode} · net {live.net} · P&amp;L (practice) ${(live.pnlCents / 100).toFixed(2)}
        </p>
      )}
      <div className="flex flex-wrap items-center gap-3">
        {FIELDS.map((f) => (
          <label key={f.key} className="flex items-center gap-1" title={f.hint}>
            <span className="text-muted-foreground">{f.label}</span>
            <input
              type="number" step={f.step} min={0} defaultValue={String(cfg[f.key] ?? '')} key={`${row?.id ?? 'draft'}-${f.key}-${cfg[f.key] ?? ''}`}
              aria-label={`harvester ${f.label}`}
              className="bg-background border-border w-20 rounded border px-1 py-0.5 font-mono text-[11px]"
              onBlur={(e) => {
                const v = Number(e.target.value);
                if (!Number.isFinite(v) || v === cfg[f.key]) return;
                if (row) void h?.patch({ [f.key]: v });
                else setDraft((d) => ({ ...d, [f.key]: v }));
              }}
            />
          </label>
        ))}
      </div>
      <div className="flex flex-wrap items-center gap-1.5">
        {!row && (
          <button type="button" disabled={control.busy || !h} className={cn(small, 'border-sky-500 bg-sky-500/15 text-sky-500')}
            onClick={() => void h?.run({ ...draft, mode: 'shadow' })}>
            start in practice
          </button>
        )}
        {row && (
          <button type="button" disabled={control.busy} className={cn(small, armed ? 'border-border text-foreground' : 'border-sky-500 bg-sky-500/15 text-sky-500')}
            onClick={() => void h?.setArm(!armed)}>
            {armed ? 'stop' : 'start'}
          </button>
        )}
        {row && (
          <button type="button" disabled={control.busy} className="text-muted-foreground hover:text-foreground text-[10px] uppercase"
            onClick={() => { if (window.confirm('Remove the harvester from this game? Its simulated position is dropped.')) void h?.remove(); }}>
            remove
          </button>
        )}
        <span className="text-muted-foreground text-[10px]">Simulated fills only — live needs its own authorization (D-065).</span>
      </div>
      {control.error && <p className="text-destructive text-[10px]">{control.error}</p>}
    </div>
  );
}
